import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IRowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment, { Moment } from 'moment';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { TranslateModule } from '@ngx-translate/core';

import { DateRangeFilter, FilterType } from '../../models/ag-grid';
import { WEEK_FORMAT_PATTERN } from '../../models/filter-formats';
import { FilterInstance } from '../../services/datacenter-filter';
import { CopoDatepickerInputDirective } from '../../directives/date-picker.directive';

import { DatacenterFilterParamsInterface } from './datacenter-date-filter.component';

interface MomentRange {
	from?: Moment;
	to?: Moment;
}

interface MomentCache extends MomentRange {
	ranges?: MomentRange[];
}

@Component({
	selector: 'copo-datacenter-date-range-filter',
	templateUrl: './datacenter-date-range-filter.component.html',
	styleUrl: './datacenter-date-range-filter.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		MatDatepickerModule,

		MatFormFieldModule,

		MatInputModule,
		CopoDatepickerInputDirective,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule
	]
})
export class DatacenterDateRangeFilterComponent implements AgFilterComponent, FilterInstance {
	@HostBinding('class.passthrough') readonly passthrough = true;
	fromDateControl = new FormControl<Date | null>(null);
	toDateControl = new FormControl<Date | null>(null);
	model: DateRangeFilter = { customFilterName: this.getCustomFilterName() };
	private isClientSide = false;
	private params?: DatacenterFilterParamsInterface;
	private cache?: MomentCache;

	agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
	}

	isFilterActive() {
		return this.model.from !== undefined
			|| this.model.to !== undefined
			|| (this.model.ranges !== undefined && this.model.ranges.length > 0);
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		const value = this.getColumnValue(filterParams.node);
		if (value === undefined) {
			return false;
		}
		if (!this.cache) {
			this.cache = this.createCache();
		}
		if (this.cache.ranges?.length && !this.cache.ranges.some(range => this.isInRange(value, range))) {
			return false;
		}
		return this.isInRange(value, this.cache);
	}

	getModel(): DateRangeFilter | null {
		if (!this.isFilterActive()) {
			return null;
		}
		return {
			...this.model,
			customFilterName: this.getCustomFilterName()
		};
	}

	setModel(model: DateRangeFilter | null) {
		this.model = {
			...model ?? { customFilterName: this.getCustomFilterName() },
			format: model?.format ?? 'date'
		};
		this.cache = undefined;
		this.fromDateControl.setValue(model?.from ?? null);
		this.toDateControl.setValue(model?.to ?? null);
	}

	onDateChange() {
		this.model = {
			...this.model,
			from: this.fromDateControl.value ?? undefined,
			to: this.toDateControl.value ?? undefined
		};
		this.updateDateBasedOnFormat();
		this.cache = undefined;
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	resetFromDate() {
		this.fromDateControl.reset();
		this.model.from = undefined;
		this.cache = undefined;
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	resetToDate() {
		this.toDateControl.reset();
		this.model.to = undefined;
		this.cache = undefined;
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	onTypeChange(event: 'date' | 'week') {
		this.model = {
			...this.model,
			format: event
		};
		this.cache = undefined;
	}

	getCustomFilterName() {
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'DOCUMENT_DATE_RANGE_FILTER') {
			return FilterType.DOCUMENT_DATE_RANGE_FILTER;
		}
		return FilterType.DATE_RANGE_FILTER;
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>): Date | undefined {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			return value instanceof Date ? value : undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			return value instanceof Date ? value : undefined;
		}
		return undefined;
	}

	private isInRange(date: Date, range: MomentRange): boolean {
		/* more readable like that */
		/* eslint-disable sonarjs/prefer-single-boolean-return */
		if (range.from?.isAfter(date)) {
			return false;
		}
		if (range.to?.isBefore(date)) {
			return false;
		}
		return true;
		/* eslint-enable sonarjs/prefer-single-boolean-return */
	}

	private updateDateBasedOnFormat() {
		// when week format, the day must be the first monday of week
		// so in that case convert the date using moment
		if (this.model.format !== 'week') {
			return;
		}
		if (this.model.from) {
			const weeklyFormat = moment(this.model.from).format(WEEK_FORMAT_PATTERN);
			this.model.from = moment(weeklyFormat).toDate();
			this.fromDateControl.setValue(this.model.from);
		}
		if (this.model.to) {
			const weeklyFormat = moment(this.model.to).format(WEEK_FORMAT_PATTERN);
			this.model.to = moment(weeklyFormat).toDate();
			this.toDateControl.setValue(this.model.to);
		}
	}

	private createCache() {
		return {
			from: this.model.from ? moment(this.model.from) : undefined,
			to: this.model.to ? moment(this.model.to) : undefined,
			ranges: this.model.ranges?.map(range => ({
				from: range.from ? moment(range.from) : undefined,
				to: range.to ? moment(range.to) : undefined,
			}))
		};
	}
}
